import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  Renderer2,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GenesysChatService } from 'src/app/service/genesys-chat.service';
import { LoggingService } from 'src/app/service/api/logging.service';
import { LoggingRequest } from 'src/app/shared/models/log';
import { TranslationRequest } from 'src/app/shared/models/TranslationRequest';

declare function doLiveChat(params): any;
declare function closeLiveChat(): any;

@Component({
  selector: 'app-support-dialog',
  template: `
    <app-dialog
      [title]="'Technical Support' | translation : translationRequest | sentenceCase "
      ariaLabel="'Technical Support dialog' | translation : translationRequest"
      [showHeader]="true"
      [showCancel]="false"
      [showConfirm]="true"
      [confirmLabel]="'Ok' | translation : translationRequest | sentenceCase "
      [btnConfirmAltText]="'Ok' | translation : translationRequest | sentenceCase "
      [btnConfirmTitleText]="'Ok' | translation : translationRequest | sentenceCase "
      [width]="600"
      (confirmEvent)="onClose($event)"
    >
      <div class="tw-p-6">
        <!-- Live Chat Section -->
        <div class="tw-mb-6">
          <span class="tw-block tw-mb-4 tw-text-gray-700">
            {{'Live Chat help is available from 8:00 a.m. until 5:00 p.m. Central Time, Monday through Friday.' | translation : translationRequest  }}
          </span>

          <div class="tw-mt-4" *ngIf="!chatLaunched && !isInitializingChat">
            <app-button
              *ngIf="!showLiveChatErrorInfo"
              emphasis="High"
              (click)="launchLiveChat()"
              arialabel="Launch Live Chat"
              id="btnLaunchLiveChat"
            >
              {{'LIVE CHAT' | translation : translationRequest | sentenceCase }}
            </app-button>
          </div>

          <!-- Loading State -->
          <div class="tw-mt-4" *ngIf="isInitializingChat">
            <div
              class="tw-flex tw-items-center tw-justify-center tw-space-x-3 tw-py-3 tw-px-4 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200"
            >
              <div
                class="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-b-2 tw-border-black"
              ></div>
              <span class="tw-text-black tw-font-medium"
                >{{'Loading Live Chat...' | translation : translationRequest | sentenceCase }}</span
              >
            </div>
          </div>

          <div class="tw-mt-4" *ngIf="showLiveChatErrorInfo && !isInitializingChat">
            <app-toast
              [message]="liveChatUnavailableMessage | translation : translationRequest"
              [type]="'Info'">
            </app-toast>
          </div>

          <div
            id="anchor"
            class="tw-relative"
            [class.tw-mt-4]="!showLiveChatErrorInfo"
            [class.tw-mt-1]="showLiveChatErrorInfo"
          ></div>
          <span
            id="displayLocation"
            class="tw-block tw-leading-[25px] tw-w-full"
          ></span>
        </div>

        <div [class.app-mt8]="!showLiveChatErrorInfo" [class.tw-mt-2]="showLiveChatErrorInfo">
          <div
            tabindex="0"
            role="button"
            class="tw-flex tw-items-center tw-cursor-pointer hover:tw-text-gray-700 tw-transition-colors app-pt2"
            id="spnmoresupportoption"
            (keyup.enter)="collapseExpand(nextAction)"
            (click)="collapseExpand(nextAction)"
            [attr.aria-expanded]="!doCollapse"
          >
            <mat-icon class="tw-mr-2">{{ termsIcon }}</mat-icon>
            <span>{{'More support options' | translation : translationRequest | sentenceCase }}</span>
          </div>

          <div
            class="tw-overflow-hidden tw-transition-all tw-duration-300"
            [class.tw-h-0]="doCollapse"
            [class.tw-h-auto]="!doCollapse"
          >
            <div class="tw-p-4 tw-mt-4 tw-bg-gray-50 tw-rounded-lg">
              <p class="tw-mb-4 tw-text-gray-700">
                {{'Sedgwick Technical Support is available from 7:00 a.m. to 6:00 p.m., Central Time, Monday through Friday.' | translation : translationRequest  }}
              </p>

              <div class="tw-space-y-2">
                <p
                  class="tw-text-gray-700"
                  aria-label="Toll free Number 8666477610 | translation : translationRequest"
                >
                  <span class="tw-font-medium">
                  {{'Toll-free number:' | translation : translationRequest | sentenceCase }}
                  </span> 
                  (866)-647-7610
                </p>

                <p class="tw-text-gray-700" aria-label="Toll Number 9014157800 | translation : translationRequest">
                  <span class="tw-font-medium">
                  {{'Toll number:' | translation : translationRequest | sentenceCase }}
                  </span> (901)
                  415-7800
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </app-dialog>
  `,
})
export class SupportDialogComponent implements OnInit, OnDestroy {
  @ViewChild('displayLocation') myDiv: ElementRef;
  doCollapse = true;
  termsIcon = 'arrow_right';
  nextAction = 'expand';
  isVisible = true;
  lhnHocButton: HTMLElement;
  isInitializingChat = false;
  chatLaunched = false;
  showLiveChatErrorInfo = false;

  readonly liveChatUnavailableMessage =
    'Error: Live chat is currently unavailable.<br></br>Please try again later or contact Technical Support at one of the numbers provided.';

  translationRequest: TranslationRequest = new TranslationRequest(
    {
      language: sessionStorage.getItem('languagepreference'), // Default to English, you can make this dynamic
      key: null,
      contNum: 0,
      tableName: null,
      primaryKey: null,
      primaryValue: null,
      lob: null,
      group: 'Shared',
      subGroup: 'Shared',
      saveToCache: false
    }
  );

  constructor(
    private renderer: Renderer2,
    private dialogRef: MatDialogRef<SupportDialogComponent>,
    private genesysChatService: GenesysChatService,
    private loggingService: LoggingService
  ) {}

  ngOnInit() {
    this.genesysChatService.setFallbackCallback((error?: string) => {
      if (this.isInitializingChat) {
        this.chatLaunched = false;
        if (error) {
          this.showLiveChatErrorInfo = true;
          this.collapseExpand('expand');
          this.logLiveChatUnavailableError(error);
        }
        this.fallbackToLiveHelpNow().finally(() => {
          this.isInitializingChat = false;
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.genesysChatService.forceResetMessengerState();
    this.onClose();
  }

  async launchLiveChat(): Promise<void> {
    this.isInitializingChat = true;
    try {
      if (!this.genesysChatService.isGenesysLoaded()) {
        await this.genesysChatService.initializeGenesys();
        if (!this.genesysChatService.isGenesysLoaded()) {
          throw new Error('Genesys failed to load');
        }
      }

      this.genesysChatService.openMessenger();
      this.chatLaunched = true;

      setTimeout(() => {
        if (
          this.genesysChatService.isGenesysLoaded() &&
          this.genesysChatService.isMessengerOpened()
        ) {
          this.isInitializingChat = false;
          this.dialogRef.close('chat_initiated');
        } else {
          if (this.isInitializingChat) {
            this.chatLaunched = false;
            this.fallbackToLiveHelpNow().finally(() => {
              this.isInitializingChat = false;
            });
          } else {
            this.isInitializingChat = false;
          }
        }
      }, 4000);
    } catch (error) {
      this.collapseExpand('expand');
      this.showLiveChatErrorInfo = true;
      this.isInitializingChat = false;
      const reason = error instanceof Error ? error.message : String(error);
      this.logLiveChatUnavailableError(reason);
      await this.fallbackToLiveHelpNow();
    }
  }

  private async fallbackToLiveHelpNow(): Promise<void> {

    try {
      const scriptLoaded = await this.loadLiveHelpNowScript();
      if (!scriptLoaded) {
        return;
      }

      await this.waitForLiveHelpNowReady();

      const passVar = { Flag: 'cs' };
      doLiveChat(passVar);

      this.chatLaunched = true;

      setTimeout(() => {
        this.directlyOpenLiveHelpNowChat();
      }, 300);
    } catch {
      this.collapseExpand('expand');
      this.showLiveChatErrorInfo = true;
    } 
  }

  private logLiveChatUnavailableError(reason: string): void {
    const logRequest = new LoggingRequest({
      message: `Support dialog live chat was unable to run. Reason: ${reason}`,
      severity: 'Error',
    });

    this.loggingService.logLiveChatUnavailableError(logRequest).subscribe();
  }

  private directlyOpenLiveHelpNowChat(): void {
    if (typeof (window as any).lhnJsSdk !== 'undefined') {
      try {
        if (typeof (window as any).lhnJsSdk.openChat === 'function') {
          (window as any).lhnJsSdk.openChat();
          return;
        }
        if (typeof (window as any).lhnJsSdk.showChat === 'function') {
          (window as any).lhnJsSdk.showChat();
          return;
        }
      } catch (error) {}
    }

    const lhnButton = document.getElementById('lhnHocButton');
    if (lhnButton) {
      lhnButton.click();
      return;
    }

    if (typeof (window as any).lhnOpenChat === 'function') {
      (window as any).lhnOpenChat();
      return;
    }
  }

  private waitForLiveHelpNowReady(): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const checkReady = () => {
        attempts++;

        if (typeof (window as any).lhnJsSdkInit === 'undefined') {
          (window as any).lhnJsSdkInit = function () {
            if (typeof (window as any).lhnJsSdk !== 'undefined') {
              (window as any).lhnJsSdk.setup = {
                application_id: '39f85b9c-f478-44c5-ae49-23e61838d74c',
                application_secret:
                  'fb27c42d4cb04359acc6b994bf7d83c527ac37c2d6a540a98a',
              };
              (window as any).lhnJsSdk.controls = [
                {
                  type: 'hoc',
                  id: 'ba75448d-cbf5-47ae-8dde-7518e69c51ec',
                },
              ];
            }
          };
        }

        if (typeof (window as any).closeLiveChat === 'undefined') {
          (window as any).closeLiveChat = function () {
            const chatElement = document.getElementById('chatclick');
            if (chatElement) {
              chatElement.style.display = 'none';
            }
          };
        }

        if (
          typeof doLiveChat !== 'undefined' &&
          typeof (window as any).lhnJsSdkInit !== 'undefined'
        ) {
          resolve();
        } else if (attempts < 20) {
          setTimeout(checkReady, 250);
        } else {
          resolve();
        }
      };
      checkReady();
    });
  }

  private loadLiveHelpNowScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (document.querySelector('script[src*="liveChat.js"]')) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = '../src/liveChat/liveChat.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }

  collapseExpand(action: string): void {
    this.doCollapse = action === 'collapse';
    this.termsIcon = this.doCollapse ? 'arrow_right' : 'arrow_drop_down';
    this.nextAction = this.doCollapse ? 'expand' : 'collapse';
    this.updateChatPosition();
  }

  private updateChatPosition(): void {
    setTimeout(() => {
      const displayLocation = document
        .getElementById('displayLocation')
        ?.getBoundingClientRect();
      const chat = document.getElementById('lhnHocButton');

      if (chat && displayLocation) {
        this.lhnHocButton = chat;
        chat.style.position = 'relative';
        chat.style.top = '80px';
        chat.style.left = '0';

        const anchor = document.getElementById('anchor');
        if (anchor && !anchor.innerHTML) {
          anchor.appendChild(chat);
        }
      }
    }, 50);
  }

  onClose(result?: string): void {
    this.isVisible = false;

    if (result !== 'chat_initiated') {
      if (this.genesysChatService.isMessengerOpened()) {
        this.genesysChatService.closeMessenger();
      }

      if (this.lhnHocButton) {
        this.renderer.setStyle(this.lhnHocButton, 'display', 'none');
        document.body.appendChild(this.lhnHocButton);
      }

      try {
        if (typeof closeLiveChat !== 'undefined') {
          closeLiveChat();
        }
      } catch (error) {}
    }

    this.dialogRef.close(result);
  }
}
